import { html, css, LitElement } from "lit";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { classMap } from "lit/directives/class-map.js";
import { customElement, state, property } from "lit/decorators.js";

import { DataManager, Sermon, Series } from "./sermon-data";

import "./loading-spinner";
import "./search-sidebar";
import "./page-controls";

import type { SearchEvent } from "./search-sidebar";

const SERMONS_JSON_PATH = "/sermons/sermons.json";

export interface FilterOpts {
	title: string|null,
	series: Series|null,
	language: string|null,
}

export const blankFilterOpts: FilterOpts = {
	title: null,
	series: null,
	language: null,
}

@customElement("sermons-list")
export class SermonsList extends LitElement {
	static styles = css`
		* {
			box-sizing: border-box;
		}

		.no-sermons,
		.fatal-error {
			text-align: center;
			position: absolute;
			top: 50%;
			left: 50%;
			transform: translate(-50%, -50%);
			font-weight: bold;
			font-size: larger;
		}

		.content {
			display: flex;
			flex-direction: column;
			flex: 1;
		}

		.list {
			padding: 0 16px;
			flex: 1;
		}
	`;

	@state()
	private isLoading = true;

	@state()
	private fatalError = false;

	@state()
	private data: DataManager|null = null;

	@state()
	private currentPageNo = 0; /* zero-based index */

	@state()
	private sermonsPerPage = 10;

	@state()
	private filterOpts: FilterOpts = blankFilterOpts;

	constructor() {
		super();

		fetch(SERMONS_JSON_PATH)
			.then(r => r.json())
			.then(json => this.data = new DataManager(json))
			.then(() => { this.isLoading = false; })
			.catch((e: unknown) => {
				this.isLoading = false;
				this.fatalError = true;
				console.error(e);
			});
	}

	private gotoPrevPage() {
		if (this.currentPageNo > 0) {
			this.currentPageNo--;
		}
	}

	private gotoNextPage() {
		if (this.data === null) { return; }

		const allSermons = [...this.data.sermons.values()];
		const totalPages = Math.ceil(allSermons.length / this.sermonsPerPage);

		if (this.currentPageNo + 1 < totalPages) {
			this.currentPageNo++;
		}
	}

	private handleSearch(ev: CustomEvent<SearchEvent>) {
		const { filterOpts } = ev.detail;

		this.filterOpts = filterOpts;
	}

	private filterSermons(opts: FilterOpts) {
		if (this.data === null) { throw new Error(); }

		const sermons = [...this.data.sermons.values()];
		return sermons.filter(s => {
			const predicates: boolean[] = [];

			if (opts.series) {
				predicates.push(s.series === opts.series);
			}

			if (opts.title) {
				predicates.push(s.name.toLowerCase().includes(opts.title.toLowerCase()));
			}

			if (opts.language) {
				predicates.push(s.langPrimary === opts.language || s.langSecondary === opts.language);
			}

			if (predicates.length === 0) {
				return true;
			}

			return predicates.every(p => p);
		});
	}

	render() {
		if (this.fatalError) {
			return html`
				<p class="fatal-error">An error has occurred. Please check again later.</p>
			`;
		}

		if (this.isLoading || !this.data) {
			return html`<loading-spinner></loading-spinner>`;
		}

		if (this.data.sermons.size === 0) {
			return html`<p class="no-sermons">No sermons found.</p>`
		}

		const allSeries = [...this.data.series.values()];

		const allLanguages = [
			...new Set(
				[...this.data.sermons.values()]
					.flatMap(s => [s.langPrimary, s.langSecondary])
					.filter((lang): lang is string => lang !== null)
			)
		].sort((a, b) => a.localeCompare(b));

		const sermons = this.filterSermons(this.filterOpts);

		const sermonStartIndex = this.currentPageNo * this.sermonsPerPage;
		const sermonEndIndex = sermonStartIndex + this.sermonsPerPage;
		const totalPages = Math.ceil(sermons.length / this.sermonsPerPage);

		return html`
			<!-- Font Awesome -->
			<link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/5.15.3/css/all.min.css" integrity="sha512-iBBXm8fW90+nuLcSKlbmrPcLa0OT92xO1BIsZ+ywDWZCvqsWgccV3gFoRBv0z+8dLJgyAHIhR35VZc2oM/gI1w==" crossorigin="anonymous" referrerpolicy="no-referrer" />

			<div class="content">
				<seach-sidebar
					.allSeries = ${allSeries}
					.allLanguages = ${allLanguages}
					@onSearch=${this.handleSearch}
				></seach-sidebar>
				<div class="list">
					${sermons
						.slice(sermonStartIndex, sermonEndIndex)
						.map(sermon => html`
							<sermon-item .sermon=${sermon}></sermon-item>`
						)
					}
				</div>
				<page-controls
					current-page = ${this.currentPageNo}
					total-pages = ${totalPages}
					@prevPage = ${this.gotoPrevPage}
					@nextPage = ${this.gotoNextPage}
				></page-controls>
			</div>
		`;
	}
}


@customElement("sermon-item")
export class SermonItem extends LitElement {
	@property({ attribute: false })
	sermon: Sermon|undefined;

	static styles = css`
		article {
			max-width: 600px;
			min-height: 100px;
			border: 1px dotted gray;
			margin: 8px 0;
			padding: 16px 8px;
			display: grid;
			grid-template-areas:
				"language    title       date"
				"nothing     series      series"
				"description description description"
				"actions     actions     actions"
			;
			grid-template-columns: auto 1fr auto;
			grid-template-rows: auto auto 1fr auto;
			gap: 8px;
			margin-left: auto;
			margin-right: auto;
			line-height: 1.5;
		}

		article > * {
			margin: 0;
		}

		h2 {
			grid-area: title;
			font-size: medium;
			font-weight: normal;
		}

		.series {
			grid-area: series;
			font-size: smaller;
			margin-top: -4px; /* disable gap */
			margin-left: 1ch;
		}

		.date {
			grid-area: date;
			letter-spacing: 0.2ch;
			font-size: smaller;
		}

		.languages {
			grid-area: language;
		}

		.description {
			grid-area: description;
			font-size: smaller;
		}

		.actions {
			grid-area: actions;
			justify-self: center;
			display: flex;
		}

		.actions a {
			text-decoration: none;
			padding: 6px 32px;
			color: black;
			transition: all 250ms;
			border-radius: 16px;
			font-size: smaller;
			letter-spacing: 0.2ch;
			position: relative;
			z-index: 1;
			background-color: wheat;
		}

		.actions a::before {
			content: "";
			position: absolute;
			left: 0;
			top: 0;
			bottom: 0;
			right: 100%;
			border-radius: 16px;
			z-index: -1;
			transition: all 250ms;
		}

		.actions a:hover {
			color: white;
		}

		.actions a:hover::before {
			background-color: hsl(263deg 45% 56%);
			right: 0;
		}

		.description > * {
			margin: 0;
		}

		.fa-external-link-alt {
			font-size: smaller;
		}
	`;

	render() {
		if (!this.sermon) { throw new Error(); }

		return html`
			<!-- Flag Icon CSS -->
			<link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/flag-icon-css/4.1.5/css/flag-icons.min.css" integrity="sha512-UwbBNAFoECXUPeDhlKR3zzWU3j8ddKIQQsDOsKhXQGdiB5i3IHEXr9kXx82+gaHigbNKbTDp3VY/G6gZqva6ZQ==" crossorigin="anonymous" referrerpolicy="no-referrer" />

			<!-- Font Awesome -->
			<link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/5.15.3/css/all.min.css" integrity="sha512-iBBXm8fW90+nuLcSKlbmrPcLa0OT92xO1BIsZ+ywDWZCvqsWgccV3gFoRBv0z+8dLJgyAHIhR35VZc2oM/gI1w==" crossorigin="anonymous" referrerpolicy="no-referrer" />

			<article data-id="${this.sermon.id}">
				<h2>
					${this.sermon.name}
				</h2>
				<div class="series">
					${this.sermon.series
						? html`
							<i class="fas fa-caret-right"></i>
							${this.sermon.series.name}
						`
						: ""
					}
				</div>
				<div class="date">
					${this.toReadableDate(this.sermon.date)}
				</div>
				<div class="languages">
					<span
						class = "flag-icon flag-icon-${this.toCountry(this.sermon.langPrimary)}"
						title = ${this.toLanguageName(this.sermon.langPrimary)}
					></span>
					${this.sermon.langSecondary
						? html`
							<span
								class = "flag-icon flag-icon-${this.toCountry(this.sermon.langSecondary)}"
								title = ${this.toLanguageName(this.sermon.langSecondary)}
							></span>`
						: null
					}
				</div>
				<div class="description">
					${this.sermon.description
						? unsafeHTML(this.sermon.description)
						: ""}
				</div>
				<div class="actions">
					<a
						href = "${this.toYoutubeLink(this.sermon.youtubeId)}"
						target = "_blank"
						rel = "noopener"
					>
						Watch <i class="fas fa-external-link-alt"></i>
					</a>
				</div>
			</article>
		`;
	}

	private toReadableDate(date: Date) {
		return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, "0")}-${date.getDate().toString().padStart(2, "0")}`;
	}

	private toCountry(language: string) {
		const langToCountry: { [index: string]: string|undefined } = {
			en: "us",
			id: "id",
		};

		const country = langToCountry[language];
		if (!country) {
			console.warn(`No country flag found for language "${language}"`);
			return "";
		}

		return country;
	}

	private toLanguageName(language: string) {
		const langToLangName: { [index: string]: string|undefined } = {
			en: "English",
			id: "Bahasa Indonesia (Indonesian)",
		};

		const name = langToLangName[language];
		if (!name) {
			console.warn(`No language name found for language "${language}"`);
			return language;
		}

		return name;
	}

	private toYoutubeLink(youtubeId: string) {
		return `https://youtu.be/${youtubeId}`;
	}
}